type SliderDirection = 'left' | 'right';
type ControlDirection = 'previous' | 'next';
type SlideSize = 1 | 2;

interface TestimonialsTickerConfig {
	wrapperSelector?: string;
	sliderSelector?: string;
	options?: {
		pixelsPerSecond?: number;
		controlDuration?: number;
		spaceBetween?: number;
		scrollBoostMultiplier?: number;
		maximumScrollBoost?: number;
		directions?: SliderDirection[];
		sizePattern?: SlideSize[];
		autoApplySizeClasses?: boolean;
	};
}

interface TrackTween {
	startOffset: number;
	distance: number;
	startTime: number;
	duration: number;
}

interface TrackDrag {
	pointerId: number;
	startX: number;
	startY: number;
	startOffset: number;
	currentRawOffset: number;
	isHorizontal: boolean | null;
}

interface TestimonialsTickerItem {
	element: HTMLElement;
	track: HTMLElement;
	direction: SliderDirection;
	originalSlides: HTMLElement[];
	boundaries: number[];
	cycleWidth: number;
	offset: number;
	scrollBoost: number;
	isManuallyControlled: boolean;
	tween?: TrackTween;
}

interface TestimonialsTickerGroup {
	wrapper: HTMLElement;
	items: TestimonialsTickerItem[];
	isVisible: boolean;
	cleanups: Array<() => void>;
}

class TestimonialsTickerController {
	private readonly generatedCloneAttribute = 'data-wv-testimonials-ticker-clone';
	private readonly defaultPixelsPerSecond = 24;
	private readonly defaultControlDuration = 480;
	private readonly defaultSpaceBetween = 20;
	private readonly defaultScrollBoostMultiplier = 2.5;
	private readonly defaultMaximumScrollBoost = 160;
	private readonly scrollBoostDecay = 420;
	private readonly defaultSizePattern: SlideSize[] = [2, 1, 1];

	private pixelsPerSecond = this.defaultPixelsPerSecond;
	private controlDuration = this.defaultControlDuration;
	private spaceBetween = this.defaultSpaceBetween;
	private scrollBoostMultiplier = this.defaultScrollBoostMultiplier;
	private maximumScrollBoost = this.defaultMaximumScrollBoost;
	private directions: SliderDirection[] = [];
	private sizePattern: SlideSize[] = [...this.defaultSizePattern];
	private autoApplySizeClasses = true;

	private groups: TestimonialsTickerGroup[] = [];
	private globalCleanups: Array<() => void> = [];
	private animationFrameId?: number;
	private lastFrameTime = 0;
	private lastScrollY = 0;
	private documentIsVisible = true;
	private prefersReducedMotion = false;

	public init(config?: TestimonialsTickerConfig): void {
		this.destroy();

		const wrapperSelector = config?.wrapperSelector ?? '.wv-testimonials-wrapper';
		const sliderSelector = config?.sliderSelector ?? '.wv-testimonials-slider';

		this.pixelsPerSecond =
			config?.options?.pixelsPerSecond ?? this.defaultPixelsPerSecond;
		this.controlDuration =
			config?.options?.controlDuration ?? this.defaultControlDuration;
		this.spaceBetween =
			config?.options?.spaceBetween ?? this.defaultSpaceBetween;
		this.scrollBoostMultiplier =
			config?.options?.scrollBoostMultiplier ??
			this.defaultScrollBoostMultiplier;
		this.maximumScrollBoost =
			config?.options?.maximumScrollBoost ?? this.defaultMaximumScrollBoost;
		this.directions = config?.options?.directions ?? [];
		this.sizePattern = config?.options?.sizePattern ?? [
			...this.defaultSizePattern,
		];
		this.autoApplySizeClasses =
			config?.options?.autoApplySizeClasses ?? true;

		const wrappers = Array.from(
			document.querySelectorAll<HTMLElement>(wrapperSelector)
		);

		wrappers.forEach((wrapper) => {
			this.initWrapper(wrapper, sliderSelector);
		});

		if (!this.groups.length) {
			return;
		}

		this.bindGlobalEvents();
		this.ensureAnimationFrame();
	}

	private initWrapper(wrapper: HTMLElement, sliderSelector: string): void {
		this.removeGeneratedSlideClones(wrapper);

		const sliderElements = Array.from(
			wrapper.querySelectorAll<HTMLElement>(sliderSelector)
		);

		const items = sliderElements
			.map((sliderElement, index) => {
				return this.createTickerItem(sliderElement, index);
			})
			.filter((item): item is TestimonialsTickerItem => item !== null);

		if (!items.length) {
			return;
		}

		const group: TestimonialsTickerGroup = {
			wrapper,
			items,
			isVisible: typeof IntersectionObserver === 'undefined',
			cleanups: [],
		};

		items.forEach((item) => {
			this.bindControls(group, item);
			this.bindSwipe(group, item);
			this.observeItemSize(group, item);
		});

		this.observeGroupVisibility(group);
		this.groups.push(group);
	}

	private createTickerItem(
		sliderElement: HTMLElement,
		index: number
	): TestimonialsTickerItem | null {
		const track = this.getTrack(sliderElement);

		if (!track) {
			return null;
		}

		this.applySliderCssVariables(sliderElement);

		if (this.autoApplySizeClasses) {
			this.applySlideSizeClasses(sliderElement);
		}

		const originalSlides = this.getSlideElements(sliderElement, false);

		if (!originalSlides.length) {
			return null;
		}

		const item: TestimonialsTickerItem = {
			element: sliderElement,
			track,
			direction: this.resolveDirection(sliderElement, index),
			originalSlides,
			boundaries: [],
			cycleWidth: 0,
			offset: 0,
			scrollBoost: 0,
			isManuallyControlled: false,
		};

		this.rebuildTrack(item);

		return item;
	}

	private rebuildTrack(item: TestimonialsTickerItem): void {
		const previousCycleWidth = item.cycleWidth;
		const previousProgress = previousCycleWidth > 0
			? this.normalizeOffset(item.offset, previousCycleWidth) / previousCycleWidth
			: null;

		this.removeGeneratedSlideClones(item.element);
		item.tween = undefined;

		item.originalSlides = this.getSlideElements(item.element, false);

		const trackStyle = window.getComputedStyle(item.track);
		const parsedGap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap);
		const gap = Number.isFinite(parsedGap) ? parsedGap : this.spaceBetween;

		let cycleWidth = 0;
		const boundaries: number[] = [];
		const slideWidths: number[] = [];

		item.originalSlides.forEach((slide) => {
			const slideWidth = slide.getBoundingClientRect().width;
			const slideStyle = window.getComputedStyle(slide);
			const marginInlineStart = Number.parseFloat(
				slideStyle.marginInlineStart
			) || 0;
			const marginInlineEnd = Number.parseFloat(
				slideStyle.marginInlineEnd
			) || 0;
			const slideOuterWidth =
				marginInlineStart + slideWidth + marginInlineEnd;

			boundaries.push(cycleWidth + marginInlineStart);
			slideWidths.push(slideOuterWidth);
			cycleWidth += slideOuterWidth + gap;
		});

		item.boundaries = boundaries;
		item.cycleWidth = cycleWidth;

		if (cycleWidth <= 0) {
			return;
		}

		const requiredTrackWidth = cycleWidth + item.element.clientWidth;
		let renderedTrackWidth = Math.max(0, cycleWidth - gap);
		let cloneIndex = 0;

		while (
			renderedTrackWidth < requiredTrackWidth &&
			cloneIndex < 200
		) {
			const sourceIndex = cloneIndex % item.originalSlides.length;
			const sourceSlide = item.originalSlides[sourceIndex];
			const sourceWidth = slideWidths[sourceIndex];

			if (!sourceSlide || typeof sourceWidth !== 'number') {
				break;
			}

			const clone = sourceSlide.cloneNode(true) as HTMLElement;

			clone.setAttribute(this.generatedCloneAttribute, 'true');
			clone.setAttribute('aria-hidden', 'true');
			clone.setAttribute('inert', '');

			this.stripIds(clone);
			item.track.appendChild(clone);

			renderedTrackWidth += gap + sourceWidth;
			cloneIndex += 1;
		}

		if (previousProgress === null) {
			item.offset = item.direction === 'right' ? cycleWidth : 0;
		} else {
			item.offset = previousProgress * cycleWidth;
		}

		this.renderItem(item);
	}

	private applySliderCssVariables(sliderElement: HTMLElement): void {
		sliderElement.style.setProperty(
			'--wv-testimonials-slider-space',
			`${this.spaceBetween}px`
		);

		sliderElement.style.setProperty(
			'--wv-testimonials-slider-size-1-gap-correction',
			`${this.spaceBetween * 0.75}px`
		);

		sliderElement.style.setProperty(
			'--wv-testimonials-slider-size-2-gap-correction',
			`${this.spaceBetween * 0.5}px`
		);
	}

	private applySlideSizeClasses(sliderElement: HTMLElement): void {
		const slides = this.getSlideElements(sliderElement, false);

		slides.forEach((slide, index) => {
			const size = this.resolveSlideSize(slide, index);

			slide.classList.remove(
				'wv-testimonial-slide--size-1',
				'wv-testimonial-slide--size-2'
			);

			slide.classList.add(`wv-testimonial-slide--size-${size}`);
		});
	}

	private resolveSlideSize(slide: HTMLElement, index: number): SlideSize {
		const dataSize = slide.dataset.size;

		if (dataSize === '2') {
			return 2;
		}

		if (dataSize === '1') {
			return 1;
		}

		if (slide.classList.contains('wv-testimonial-slide--size-2')) {
			return 2;
		}

		if (slide.classList.contains('wv-testimonial-slide--size-1')) {
			return 1;
		}

		return this.sizePattern[index % this.sizePattern.length] ?? 1;
	}

	private resolveDirection(
		sliderElement: HTMLElement,
		index: number
	): SliderDirection {
		const configuredDirection = this.directions[index];

		if (configuredDirection === 'left' || configuredDirection === 'right') {
			return configuredDirection;
		}

		const htmlDirection = sliderElement.dataset.direction;

		if (htmlDirection === 'left' || htmlDirection === 'right') {
			return htmlDirection;
		}

		return index % 2 === 1 ? 'right' : 'left';
	}

	private bindControls(
		group: TestimonialsTickerGroup,
		item: TestimonialsTickerItem
	): void {
		const prevButton = item.element.querySelector<HTMLButtonElement>(
			'.wv-testimonials-slider__control--prev'
		);
		const nextButton = item.element.querySelector<HTMLButtonElement>(
			'.wv-testimonials-slider__control--next'
		);

		const controls = item.element.querySelector<HTMLElement>(
			'.wv-testimonials-slider__controls'
		);

		const controlsAreUseful = item.originalSlides.length > 1;

		if (prevButton) {
			prevButton.disabled = !controlsAreUseful;

			const onPrevClick = (event: MouseEvent) => {
				event.preventDefault();
				this.stopAutomaticMovement(item);
				this.moveWithControl(item, 'previous');
			};

			prevButton.addEventListener('click', onPrevClick);
			group.cleanups.push(() => {
				prevButton.removeEventListener('click', onPrevClick);
			});
		}

		if (nextButton) {
			nextButton.disabled = !controlsAreUseful;

			const onNextClick = (event: MouseEvent) => {
				event.preventDefault();
				this.stopAutomaticMovement(item);
				this.moveWithControl(item, 'next');
			};

			nextButton.addEventListener('click', onNextClick);
			group.cleanups.push(() => {
				nextButton.removeEventListener('click', onNextClick);
			});
		}

		if (controls) {
			const onControlsFocus = () => {
				this.stopAutomaticMovement(item);
			};

			controls.addEventListener('focusin', onControlsFocus);
			group.cleanups.push(() => {
				controls.removeEventListener('focusin', onControlsFocus);
			});
		}
	}

	private stopAutomaticMovement(item: TestimonialsTickerItem): void {
		if (item.isManuallyControlled) {
			return;
		}

		item.isManuallyControlled = true;
		item.scrollBoost = 0;
		item.element.classList.add('wv-testimonials-slider--manually-controlled');
		item.element.dataset.autoplay = 'stopped';
	}

	private bindSwipe(
		group: TestimonialsTickerGroup,
		item: TestimonialsTickerItem
	): void {
		const mobileViewport = window.matchMedia('(max-width: 767px)');
		let drag: TrackDrag | undefined;

		const onPointerDown = (event: PointerEvent) => {
			if (
				!mobileViewport.matches ||
				!event.isPrimary ||
				event.pointerType === 'mouse' ||
				item.cycleWidth <= 0
			) {
				return;
			}

			item.tween = undefined;

			drag = {
				pointerId: event.pointerId,
				startX: event.clientX,
				startY: event.clientY,
				startOffset: item.offset,
				currentRawOffset: item.offset,
				isHorizontal: null,
			};
		};

		const onPointerMove = (event: PointerEvent) => {
			if (!drag || event.pointerId !== drag.pointerId) {
				return;
			}

			let distanceX = event.clientX - drag.startX;
			const distanceY = event.clientY - drag.startY;

			if (drag.isHorizontal === null) {
				if (Math.max(Math.abs(distanceX), Math.abs(distanceY)) < 8) {
					return;
				}

				drag.isHorizontal = Math.abs(distanceX) > Math.abs(distanceY);

				if (!drag.isHorizontal) {
					return;
				}

				this.stopAutomaticMovement(item);
				item.element.classList.add('wv-testimonials-slider--is-dragging');

				if (typeof item.element.setPointerCapture === 'function') {
					item.element.setPointerCapture(event.pointerId);
				}

				drag.startX = event.clientX;
				drag.startY = event.clientY;
				drag.startOffset = item.offset;
				drag.currentRawOffset = item.offset;
				distanceX = 0;
			}

			if (!drag.isHorizontal) {
				return;
			}

			event.preventDefault();

			drag.currentRawOffset = drag.startOffset - distanceX;
			item.offset = this.normalizeOffset(
				drag.currentRawOffset,
				item.cycleWidth
			);

			this.renderItem(item);
		};

		const finishPointerInteraction = (event: PointerEvent) => {
			if (!drag || event.pointerId !== drag.pointerId) {
				return;
			}

			if (drag.isHorizontal) {
				const distanceX = event.clientX - drag.startX;
				const swipeThreshold = Math.min(48, item.element.clientWidth * 0.12);
				let targetRawOffset = drag.startOffset;

				if (Math.abs(distanceX) >= swipeThreshold) {
					const direction: ControlDirection =
						distanceX < 0 ? 'next' : 'previous';

					targetRawOffset += this.getControlDistance(
						item,
						this.normalizeOffset(drag.startOffset, item.cycleWidth),
						direction
					);
				}

				this.animateItem(
					item,
					item.offset,
					targetRawOffset - drag.currentRawOffset
				);

				item.element.classList.remove('wv-testimonials-slider--is-dragging');

				if (
					typeof item.element.hasPointerCapture === 'function' &&
					item.element.hasPointerCapture(event.pointerId)
				) {
					item.element.releasePointerCapture(event.pointerId);
				}
			}

			drag = undefined;
		};

		item.element.addEventListener('pointerdown', onPointerDown);
		item.element.addEventListener('pointermove', onPointerMove);
		item.element.addEventListener('pointerup', finishPointerInteraction);
		item.element.addEventListener('pointercancel', finishPointerInteraction);

		group.cleanups.push(() => {
			item.element.removeEventListener('pointerdown', onPointerDown);
			item.element.removeEventListener('pointermove', onPointerMove);
			item.element.removeEventListener('pointerup', finishPointerInteraction);
			item.element.removeEventListener('pointercancel', finishPointerInteraction);
			item.element.classList.remove('wv-testimonials-slider--is-dragging');
		});
	}

	private moveWithControl(
		item: TestimonialsTickerItem,
		direction: ControlDirection
	): void {
		if (item.cycleWidth <= 0 || item.boundaries.length < 2) {
			return;
		}

		const currentOffset = this.normalizeOffset(item.offset, item.cycleWidth);
		const distance = this.getControlDistance(item, currentOffset, direction);

		this.animateItem(item, currentOffset, distance);
	}

	private getControlDistance(
		item: TestimonialsTickerItem,
		currentOffset: number,
		direction: ControlDirection
	): number {
		const epsilon = 1;
		let distance = 0;

		if (direction === 'next') {
			const nextBoundary = item.boundaries.find((boundary) => {
				return boundary > currentOffset + epsilon;
			});

			distance = (nextBoundary ?? item.cycleWidth) - currentOffset;
		} else {
			const previousBoundary = [...item.boundaries]
				.reverse()
				.find((boundary) => {
					return boundary < currentOffset - epsilon;
				});

			const targetOffset = previousBoundary ??
				(item.boundaries[item.boundaries.length - 1] - item.cycleWidth);

			distance = targetOffset - currentOffset;
		}

		return distance;
	}

	private animateItem(
		item: TestimonialsTickerItem,
		startOffset: number,
		distance: number
	): void {
		if (this.prefersReducedMotion) {
			item.offset = this.normalizeOffset(
				startOffset + distance,
				item.cycleWidth
			);
			item.tween = undefined;
			this.renderItem(item);
			return;
		}

		item.tween = {
			startOffset,
			distance,
			startTime: performance.now(),
			duration: this.controlDuration,
		};

		this.ensureAnimationFrame();
	}

	private observeGroupVisibility(group: TestimonialsTickerGroup): void {
		if (typeof IntersectionObserver === 'undefined') {
			group.isVisible = true;
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];

				group.isVisible = Boolean(entry?.isIntersecting);

				if (group.isVisible) {
					this.ensureAnimationFrame();
				} else {
					group.items.forEach((item) => {
						item.element.classList.remove(
							'wv-testimonials-slider--is-animating'
						);
					});
				}
			},
			{
				rootMargin: '160px 0px',
				threshold: 0,
			}
		);

		observer.observe(group.wrapper);

		group.cleanups.push(() => {
			observer.disconnect();
		});
	}

	private observeItemSize(
		group: TestimonialsTickerGroup,
		item: TestimonialsTickerItem
	): void {
		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		let refreshFrameId: number | undefined;

		const observer = new ResizeObserver(() => {
			if (typeof refreshFrameId === 'number') {
				window.cancelAnimationFrame(refreshFrameId);
			}

			refreshFrameId = window.requestAnimationFrame(() => {
				refreshFrameId = undefined;
				this.rebuildTrack(item);
				this.ensureAnimationFrame();
			});
		});

		observer.observe(item.element);

		group.cleanups.push(() => {
			observer.disconnect();

			if (typeof refreshFrameId === 'number') {
				window.cancelAnimationFrame(refreshFrameId);
			}
		});
	}

	private bindGlobalEvents(): void {
		this.lastScrollY = window.scrollY;
		this.documentIsVisible = !document.hidden;

		const motionPreference = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		);

		const syncMotionPreference = () => {
			const shouldReduceMotion =
				motionPreference.matches ||
				document.documentElement.classList.contains('a11y-reduce-motion');

			this.prefersReducedMotion = shouldReduceMotion;

			if (shouldReduceMotion) {
				this.groups.forEach((group) => {
					group.items.forEach((item) => {
						item.scrollBoost = 0;
					});
				});
			}

			this.ensureAnimationFrame();
		};

		syncMotionPreference();

		const onMotionPreferenceChange = () => {
			syncMotionPreference();
		};

		motionPreference.addEventListener('change', onMotionPreferenceChange);
		this.globalCleanups.push(() => {
			motionPreference.removeEventListener('change', onMotionPreferenceChange);
		});

		const motionClassObserver = new MutationObserver(() => {
			syncMotionPreference();
		});

		motionClassObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class'],
		});

		this.globalCleanups.push(() => {
			motionClassObserver.disconnect();
		});

		const onVisibilityChange = () => {
			this.documentIsVisible = !document.hidden;

			if (this.documentIsVisible) {
				this.lastFrameTime = 0;
				this.ensureAnimationFrame();
			} else {
				this.cancelAnimationFrame();
			}
		};

		document.addEventListener('visibilitychange', onVisibilityChange);
		this.globalCleanups.push(() => {
			document.removeEventListener('visibilitychange', onVisibilityChange);
		});

		const onScroll = () => {
			const currentScrollY = window.scrollY;
			const scrollDistance = Math.min(
				160,
				Math.abs(currentScrollY - this.lastScrollY)
			);

			this.lastScrollY = currentScrollY;

			if (this.prefersReducedMotion || scrollDistance <= 0) {
				return;
			}

			this.groups.forEach((group) => {
				if (!group.isVisible) {
					return;
				}

				group.items.forEach((item) => {
					if (item.isManuallyControlled) {
						return;
					}

					item.scrollBoost = Math.min(
						this.maximumScrollBoost,
						item.scrollBoost + scrollDistance * this.scrollBoostMultiplier
					);
				});
			});

			this.ensureAnimationFrame();
		};

		window.addEventListener('scroll', onScroll, {passive: true});
		this.globalCleanups.push(() => {
			window.removeEventListener('scroll', onScroll);
		});
	}

	private ensureAnimationFrame(): void {
		if (
			typeof this.animationFrameId === 'number' ||
			!this.documentIsVisible ||
			!this.hasActiveAnimation()
		) {
			return;
		}

		this.animationFrameId = window.requestAnimationFrame((time) => {
			this.animate(time);
		});
	}

	private animate(time: number): void {
		this.animationFrameId = undefined;

		const elapsedSeconds = this.lastFrameTime > 0
			? Math.min((time - this.lastFrameTime) / 1000, 0.064)
			: 0;

		this.lastFrameTime = time;

		let needsNextFrame = false;

		this.groups.forEach((group) => {
			group.items.forEach((item) => {
				let itemIsAnimating = false;

				if (item.tween) {
					this.updateTween(item, time);
					itemIsAnimating = Boolean(item.tween);
					needsNextFrame = needsNextFrame || itemIsAnimating;
				}

				const canMoveAutomatically =
					group.isVisible &&
					!this.prefersReducedMotion &&
					!item.isManuallyControlled &&
					item.cycleWidth > 0;

				if (canMoveAutomatically) {
					const directionMultiplier = item.direction === 'left' ? 1 : -1;
					const currentSpeed = this.pixelsPerSecond + item.scrollBoost;

					item.offset = this.normalizeOffset(
						item.offset +
							currentSpeed * elapsedSeconds * directionMultiplier,
						item.cycleWidth
					);

					item.scrollBoost *= Math.exp(
						-(elapsedSeconds * 1000) / this.scrollBoostDecay
					);

					if (item.scrollBoost < 0.1) {
						item.scrollBoost = 0;
					}

					this.renderItem(item);
					itemIsAnimating = true;
					needsNextFrame = true;
				}

				item.element.classList.toggle(
					'wv-testimonials-slider--is-animating',
					itemIsAnimating
				);
			});
		});

		if (needsNextFrame && this.documentIsVisible) {
			this.animationFrameId = window.requestAnimationFrame((nextTime) => {
				this.animate(nextTime);
			});
		} else {
			this.lastFrameTime = 0;
		}
	}

	private updateTween(item: TestimonialsTickerItem, time: number): void {
		const tween = item.tween;

		if (!tween) {
			return;
		}

		const progress = Math.min(
			1,
			Math.max(0, (time - tween.startTime) / tween.duration)
		);
		const easedProgress = 1 - Math.pow(1 - progress, 3);

		item.offset = this.normalizeOffset(
			tween.startOffset + tween.distance * easedProgress,
			item.cycleWidth
		);

		this.renderItem(item);

		if (progress >= 1) {
			item.tween = undefined;
		}
	}

	private hasActiveAnimation(): boolean {
		return this.groups.some((group) => {
			return group.items.some((item) => {
				if (item.tween) {
					return true;
				}

				return (
					group.isVisible &&
					!this.prefersReducedMotion &&
					!item.isManuallyControlled &&
					item.cycleWidth > 0
				);
			});
		});
	}

	private cancelAnimationFrame(): void {
		if (typeof this.animationFrameId === 'number') {
			window.cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = undefined;
		}

		this.lastFrameTime = 0;
	}

	private renderItem(item: TestimonialsTickerItem): void {
		item.track.style.transform = `translate3d(${-item.offset}px, 0, 0)`;
	}

	private normalizeOffset(offset: number, cycleWidth: number): number {
		if (cycleWidth <= 0) {
			return 0;
		}

		return ((offset % cycleWidth) + cycleWidth) % cycleWidth;
	}

	private getTrack(sliderElement: HTMLElement): HTMLElement | null {
		return sliderElement.querySelector<HTMLElement>(
			'.wv-testimonials-slider__swiper-wrapper'
		);
	}

	private getSlideElements(
		sliderElement: HTMLElement,
		includeGeneratedClones: boolean
	): HTMLElement[] {
		const track = this.getTrack(sliderElement);

		if (!track) {
			return [];
		}

		return Array.from(track.children).filter(
			(child): child is HTMLElement => {
				if (!(child instanceof HTMLElement)) {
					return false;
				}

				if (!child.classList.contains('wv-testimonial-slide--swiper-slide')) {
					return false;
				}

				if (
					!includeGeneratedClones &&
					child.getAttribute(this.generatedCloneAttribute) === 'true'
				) {
					return false;
				}

				return true;
			}
		);
	}

	private removeGeneratedSlideClones(root: HTMLElement): void {
		root.querySelectorAll<HTMLElement>(
			`[${this.generatedCloneAttribute}="true"]`
		).forEach((clone) => {
			clone.remove();
		});
	}

	private stripIds(element: HTMLElement): void {
		element.removeAttribute('id');

		element.querySelectorAll<HTMLElement>('[id]').forEach((child) => {
			child.removeAttribute('id');
		});
	}

	public destroy(): void {
		this.cancelAnimationFrame();

		this.globalCleanups.forEach((cleanup) => {
			cleanup();
		});

		this.groups.forEach((group) => {
			group.cleanups.forEach((cleanup) => {
				cleanup();
			});

			group.items.forEach((item) => {
				this.removeGeneratedSlideClones(item.element);

				item.track.style.removeProperty('transform');
				item.element.style.removeProperty('--wv-testimonials-slider-space');
				item.element.style.removeProperty(
					'--wv-testimonials-slider-size-1-gap-correction'
				);
				item.element.style.removeProperty(
					'--wv-testimonials-slider-size-2-gap-correction'
				);
				item.element.classList.remove(
					'wv-testimonials-slider--is-animating',
					'wv-testimonials-slider--manually-controlled'
				);
				delete item.element.dataset.autoplay;
			});
		});

		this.groups = [];
		this.globalCleanups = [];
	}
}

export const TestimonialsSlider = new TestimonialsTickerController();
